import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import dayjs from 'dayjs';
import EventCard from './EventCard';

export interface CalendarEvent {
  id: string | number;
  title: string;
  start: Date;
  end: Date;
  color?: string;
  categoryColors?: string[];
  startTime?: string;
  endTime?: string;
  customer?: string;
  brand?: string;
  address?: string;
  status?: string;
  stage?: string;
  [key: string]: any;
}

interface WeekTimelineViewProps {
  events: CalendarEvent[];
  selectedDate: Date;
  numberOfDays?: number; // 1 for day view, 7 for week view
  startHour?: number;
  endHour?: number;
  weekStartDay?: number; // 0 = Sunday
  onPressCell?: (date: Date) => void;
  onPressEvent?: (event: CalendarEvent) => void;
}

const PRIMARY = '#2e96e1';
const HOUR_HEIGHT = 90;
const TIME_COLUMN_WIDTH = 56;
const EVENT_SPACING = 3;

function assignColumns(events: CalendarEvent[]) {
  if (events.length === 0) return [] as { event: CalendarEvent; col: number; totalCols: number }[];
  const sorted = [...events].sort((a, b) => a.start.getTime() - b.start.getTime());
  const columns: { endTime: number }[] = [];
  const assignments: { event: CalendarEvent; col: number }[] = [];
  for (const event of sorted) {
    const startMs = event.start.getTime();
    const endMs = event.end.getTime();
    let placed = false;
    for (let c = 0; c < columns.length; c++) {
      if (columns[c].endTime <= startMs) {
        columns[c].endTime = endMs;
        assignments.push({ event, col: c });
        placed = true;
        break;
      }
    }
    if (!placed) {
      columns.push({ endTime: endMs });
      assignments.push({ event, col: columns.length - 1 });
    }
  }
  return assignments.map(({ event, col }) => {
    const startMs = event.start.getTime();
    const endMs = event.end.getTime();
    const overlapping = assignments.filter(a => {
      const aStart = a.event.start.getTime();
      const aEnd = a.event.end.getTime();
      return aStart < endMs && aEnd > startMs;
    });
    const maxCol = overlapping.reduce((m, a) => Math.max(m, a.col), 0);
    return { event, col, totalCols: maxCol + 1 };
  });
}

const WeekTimelineView: React.FC<WeekTimelineViewProps> = ({
  events,
  selectedDate,
  numberOfDays = 7,
  startHour = 0,
  endHour = 24,
  weekStartDay = 0,
  onPressCell,
  onPressEvent,
}) => {
  const [now, setNow] = useState(dayjs());

  useEffect(() => {
    const t = setInterval(() => setNow(dayjs()), 60000);
    return () => clearInterval(t);
  }, []);

  const totalGridHeight = (endHour - startHour) * HOUR_HEIGHT;

  const nowDecimal = now.hour() + now.minute() / 60;
  const nowTop = nowDecimal >= startHour && nowDecimal <= endHour
    ? (nowDecimal - startHour) * HOUR_HEIGHT
    : null;

  const hours = useMemo(() => {
    const r: number[] = [];
    for (let h = startHour; h < endHour; h++) r.push(h);
    return r;
  }, [startHour, endHour]);

  const days = useMemo(() => {
    const r: dayjs.Dayjs[] = [];
    const current = dayjs(selectedDate);
    if (numberOfDays === 1) {
      r.push(current);
      return r;
    }
    let daysBack = current.day() - weekStartDay;
    if (daysBack < 0) daysBack += 7;
    const weekStart = current.subtract(daysBack, 'day');
    for (let i = 0; i < numberOfDays; i++) r.push(weekStart.add(i, 'day'));
    return r;
  }, [selectedDate, numberOfDays, weekStartDay]);

  const today = dayjs().format('YYYY-MM-DD');

  const formatHour = (hour: number) => {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    if (hour < 12) return `${hour} AM`;
    return `${hour - 12} PM`;
  };

  const getEventsForDay = (day: dayjs.Dayjs) =>
    events.filter(ev => {
      const dayStart = day.startOf('day');
      const dayEnd = day.endOf('day');
      return dayjs(ev.start).isBefore(dayEnd) && dayjs(ev.end).isAfter(dayStart);
    });

  const getEventPos = (event: CalendarEvent, day: dayjs.Dayjs) => {
    const startsBefore = dayjs(event.start).isBefore(day.startOf('day'));
    const endsAfter = dayjs(event.end).isAfter(day.endOf('day'));
    let s = startsBefore
      ? startHour
      : dayjs(event.start).hour() + dayjs(event.start).minute() / 60;
    let e = endsAfter
      ? endHour
      : dayjs(event.end).hour() + dayjs(event.end).minute() / 60;
    if (s < startHour) s = startHour;
    if (e > endHour) e = endHour;
    const top = (s - startHour) * HOUR_HEIGHT;
    const height = Math.max((e - s) * HOUR_HEIGHT - EVENT_SPACING, 22);
    return { top, height };
  };

  const handleCellPress = (day: dayjs.Dayjs, hour: number, minute: number) => {
    onPressCell?.(day.hour(hour).minute(minute).second(0).toDate());
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={[styles.timeColumn, styles.headerCell]} />
        {days.map((day, i) => {
          const isToday = day.format('YYYY-MM-DD') === today;
          return (
            <View key={i} style={styles.dayHeader}>
              <Text style={[styles.dayName, isToday && styles.todayText]}>
                {day.format('ddd').toUpperCase()}
              </Text>
              <View style={[styles.dayNumberWrapper, isToday && styles.todayCircle]}>
                <Text style={[styles.dayNumber, isToday && styles.todayNumberText]}>
                  {day.format('D')}
                </Text>
              </View>
            </View>
          );
        })}
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.grid}>
          <View style={[styles.timeColumn, { height: totalGridHeight }]}>
            {hours.map(hour => (
              <View key={hour} style={{ height: HOUR_HEIGHT }}>
                {[0, 15, 30, 45].map(min => (
                  <View key={min} style={[styles.timeSlot, { height: HOUR_HEIGHT / 4 }]}>
                    {min === 0 ? (
                      <Text style={styles.timeText}>{formatHour(hour)}</Text>
                    ) : (
                      <Text style={styles.timeTextMinor}>{`:${String(min).padStart(2, '0')}`}</Text>
                    )}
                  </View>
                ))}
              </View>
            ))}
          </View>

          {days.map((day, dayIndex) => {
            const isToday = day.format('YYYY-MM-DD') === today;
            const dayEvents = getEventsForDay(day);
            const assigned = assignColumns(dayEvents);
            return (
              <View
                key={dayIndex}
                style={[styles.dayColumn, { height: totalGridHeight }, isToday && styles.todayColumn]}
              >
                {hours.map(hour => (
                  <View key={hour} style={{ height: HOUR_HEIGHT }}>
                    {[0, 15, 30, 45].map(min => (
                      <TouchableOpacity
                        key={min}
                        style={[
                          styles.quarterSlot,
                          { height: HOUR_HEIGHT / 4 },
                          min === 0
                            ? styles.quarterSlotHour
                            : min === 30
                              ? styles.quarterSlotHalf
                              : styles.quarterSlotMinor,
                        ]}
                        onPress={() => handleCellPress(day, hour, min)}
                        activeOpacity={0.3}
                      />
                    ))}
                  </View>
                ))}

                {assigned.map(({ event, col, totalCols }) => {
                  const { top, height } = getEventPos(event, day);
                  const widthPct = 100 / totalCols;
                  const leftPct = col * widthPct;
                  const startD = dayjs(event.start);
                  const endD = dayjs(event.end);
                  const earlier = startD.isAfter(endD) ? endD : startD;
                  const later = startD.isAfter(endD) ? startD : endD;
                  const sameDay = earlier.isSame(later, 'day');
                  const compact = height < 100;
                  const showTime = height >= 56;
                  const showDateRange = !sameDay && height >= 78;
                  const showCustomer = height >= (sameDay ? 88 : 116);
                  const showBrand = height >= (sameDay ? 116 : 144);
                  const showStatus = height >= (sameDay ? 138 : 168);
                  return (
                    <View
                      key={event.id}
                      style={{
                        position: 'absolute',
                        top: top + EVENT_SPACING,
                        height,
                        left: `${leftPct}%` as any,
                        width: `${widthPct - 1}%` as any,
                        paddingLeft: col > 0 ? 1 : 0,
                      }}
                    >
                      <EventCard
                        event={event}
                        density={compact ? 'compact' : 'full'}
                        onPress={() => onPressEvent?.(event)}
                        showTime={showTime}
                        showDateRange={showDateRange}
                        showCustomer={showCustomer}
                        showBrand={showBrand}
                        showStatus={showStatus}
                        style={styles.eventFill}
                      />
                    </View>
                  );
                })}

                {isToday && nowTop !== null && (
                  <View style={[styles.nowLine, { top: nowTop }]} pointerEvents="none">
                    <View style={styles.nowDot} />
                    <View style={styles.nowLineFill} />
                  </View>
                )}
              </View>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, width: '100%', backgroundColor: '#fff' },
  header: {
    flexDirection: 'row',
    width: '100%',
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
    backgroundColor: '#fff',
  },
  headerCell: { borderRightWidth: 1, borderRightColor: '#E0E0E0' },
  dayHeader: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 8,
    borderRightWidth: 1,
    borderRightColor: '#E0E0E0',
  },
  dayName: { fontSize: 13, color: '#888', marginBottom: 4, letterSpacing: 0.5, fontWeight: '500' },
  dayNumberWrapper: {
    width: 34, height: 34, borderRadius: 17,
    alignItems: 'center', justifyContent: 'center',
  },
  todayCircle: { backgroundColor: PRIMARY },
  dayNumber: { fontSize: 18, color: '#111', fontWeight: '500' },
  todayText: { color: PRIMARY },
  todayNumberText: { color: '#fff' },
  scrollView: { flex: 1, width: '100%' },
  scrollContent: { flexGrow: 1, width: '100%' },
  grid: { flexDirection: 'row', paddingTop: 6, width: '100%' },
  timeColumn: { width: TIME_COLUMN_WIDTH, backgroundColor: '#fff' },
  timeSlot: {
    justifyContent: 'flex-start',
    paddingRight: 6,
    alignItems: 'flex-end',
    overflow: 'visible',
  },
  timeText: { fontSize: 12, lineHeight: 12, color: '#333', fontWeight: '500', marginTop: -6 },
  timeTextMinor: { fontSize: 10, lineHeight: 10, color: '#888', marginTop: -5 },
  dayColumn: {
    flex: 1,
    position: 'relative',
    borderRightWidth: 1,
    borderRightColor: '#E0E0E0',
  },
  todayColumn: { backgroundColor: '#FAFBFF' },
  quarterSlot: { width: '100%' },
  quarterSlotHour: { borderTopWidth: 1, borderTopColor: '#E0E0E0' },
  quarterSlotHalf: { borderTopWidth: 1, borderTopColor: '#EEEEEE' },
  quarterSlotMinor: { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: '#F5F5F5' },
  eventFill: { flex: 1 },
  nowLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 10,
  },
  nowDot: {
    width: 8, height: 8, borderRadius: 4,
    backgroundColor: '#E53935', marginLeft: -4,
  },
  nowLineFill: { flex: 1, height: 1.5, backgroundColor: '#E53935' },
});

export default WeekTimelineView;
